export const WORKSHOP_HOURS_PER_DAY = 8;
export const MIN_WORKSHOP_DAYS = 1;
export const MAX_WORKSHOP_DAYS = 10;
export const DEFAULT_WORKSHOP_TOPIC_MINUTES = 45;

/** Optional overrides; unknown topics use DEFAULT_WORKSHOP_TOPIC_MINUTES. */
export const workshopTopicDurationByName: Record<string, number> = {};

/** Editable per-day start (persisted on create draft). */
export interface WorkshopDayTiming {
  dayIndex: number;
  startDate: string;
  startTime: string;
}

export interface WorkshopDaySchedule {
  dayIndex: number;
  dayLabel: string;
  topics: string[];
  durationMinutes: number;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

export interface WorkshopScheduleResult {
  daySchedules: WorkshopDaySchedule[];
  totalDurationMinutes: number;
  endDate: string;
  endTime: string;
}

export type WorkshopScheduleOptions = {
  /** Per-day start date/time overrides (by dayIndex). */
  dayTimings?: WorkshopDayTiming[];
  /** Topic title → planned minutes (from Prepare/Planner). */
  durationByTopic?: Record<string, number>;
};

function toDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toTimeString(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

function addCalendarDays(dateStr: string, days: number): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return toDateString(date);
}

export function addMinutesToDateTime(
  startDate: string,
  startTime: string,
  minutes: number,
): { endDate: string; endTime: string } {
  const start = new Date(`${startDate}T${startTime}:00`);
  if (Number.isNaN(start.getTime())) {
    return { endDate: startDate, endTime: startTime };
  }
  const end = new Date(start.getTime() + minutes * 60_000);
  return { endDate: toDateString(end), endTime: toTimeString(end) };
}

export function getWorkshopTopicDurationMinutes(
  topicName: string,
  durationByTopic?: Record<string, number>,
): number {
  const override = durationByTopic?.[topicName];
  if (typeof override === 'number' && override > 0) return override;
  return workshopTopicDurationByName[topicName] ?? DEFAULT_WORKSHOP_TOPIC_MINUTES;
}

export function sumWorkshopDayTopicMinutes(
  topics: string[],
  durationByTopic?: Record<string, number>,
): number {
  return topics.reduce(
    (sum, topic) => sum + getWorkshopTopicDurationMinutes(topic, durationByTopic),
    0,
  );
}

/** Default consecutive calendar days sharing the workshop start time. */
export function buildDefaultDayTimings(
  startDate: string,
  startTime: string,
  dayCount: number,
): WorkshopDayTiming[] {
  return Array.from({ length: Math.max(0, dayCount) }, (_, dayIndex) => ({
    dayIndex,
    startDate: addCalendarDays(startDate, dayIndex),
    startTime,
  }));
}

export function mergeDayTimings(
  dayCount: number,
  startDate: string,
  startTime: string,
  existing?: WorkshopDayTiming[] | null,
): WorkshopDayTiming[] {
  const defaults = buildDefaultDayTimings(startDate, startTime, dayCount);
  if (!existing?.length) return defaults;
  const byIndex = new Map(existing.map((d) => [d.dayIndex, d]));
  return defaults.map((fallback) => {
    const prev = byIndex.get(fallback.dayIndex);
    if (!prev) return fallback;
    return {
      dayIndex: fallback.dayIndex,
      startDate: prev.startDate || fallback.startDate,
      startTime: prev.startTime || fallback.startTime,
    };
  });
}

export function computeWorkshopSchedule(
  startDate: string,
  startTime: string,
  dayTopics: string[][],
  options?: WorkshopScheduleOptions,
): WorkshopScheduleResult {
  const timings = mergeDayTimings(
    dayTopics.length,
    startDate,
    startTime,
    options?.dayTimings,
  );
  const timingByDay = new Map(timings.map((t) => [t.dayIndex, t]));

  const daySchedules: WorkshopDaySchedule[] = dayTopics.map((topics, dayIndex) => {
    const durationMinutes = sumWorkshopDayTopicMinutes(
      topics,
      options?.durationByTopic,
    );
    const timing = timingByDay.get(dayIndex);
    const dayStartDate = timing?.startDate || addCalendarDays(startDate, dayIndex);
    const dayStartTime = timing?.startTime || startTime;
    const { endDate, endTime } = addMinutesToDateTime(
      dayStartDate,
      dayStartTime,
      Math.max(durationMinutes, 0),
    );

    return {
      dayIndex,
      dayLabel: `Day ${dayIndex + 1}`,
      topics,
      durationMinutes,
      startDate: dayStartDate,
      startTime: dayStartTime,
      endDate,
      endTime,
    };
  });

  const totalDurationMinutes = daySchedules.reduce(
    (sum, day) => sum + day.durationMinutes,
    0,
  );
  const lastDay = daySchedules[daySchedules.length - 1];

  return {
    daySchedules,
    totalDurationMinutes,
    endDate: lastDay?.endDate ?? startDate,
    endTime: lastDay?.endTime ?? startTime,
  };
}

/** Resize day → topics grid. New days start empty (no default topic). */
export function resizeWorkshopDayTopics(
  days: number,
  current: string[][],
  _fallback?: string,
): string[][] {
  return Array.from({ length: days }, (_, index) => current[index] ?? []);
}

export function formatWorkshopTopicCovered(dayTopics: string[][]): string {
  return dayTopics
    .map((topics, index) => `Day ${index + 1}: ${topics.join(', ')}`)
    .join(' | ');
}

export function flattenWorkshopDayTopics(dayTopics: string[][]): string[] {
  return dayTopics.flat();
}

/** Rebuild dayTopics from ordered topics tagged with workshopDayIndex. */
export function groupTopicsByWorkshopDay(
  topics: { title: string; workshopDayIndex?: number }[],
  dayCount: number,
): string[][] {
  const days: string[][] = Array.from({ length: Math.max(0, dayCount) }, () => []);
  for (const topic of topics) {
    const dayIndex =
      typeof topic.workshopDayIndex === 'number' && topic.workshopDayIndex >= 0
        ? Math.min(topic.workshopDayIndex, Math.max(dayCount - 1, 0))
        : 0;
    if (!days[dayIndex]) days[dayIndex] = [];
    days[dayIndex].push(topic.title);
  }
  return days;
}

/** @deprecated Use computeWorkshopSchedule for topic-based duration. */
export function workshopDurationMinutes(days: number): number {
  return days * WORKSHOP_HOURS_PER_DAY * 60;
}
